import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { StackedSidebarService } from '../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { NotificationService } from '../../../../services/notifications.service';
import { BaseService } from '../../../../services/base.service';
import { ActivatedRoute, Router } from '@angular/router';
import { APPLICATION_TYPE } from './market.interface';
import { MarketService } from '../../../../services/market.service';
import { parseCurrency } from '../../../../helpers';
import { ModalService } from '../../../../services/modal.service';
import { APPLICATION_STATUS, DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, PERMISSIONS } from '../../../../enum/constants';
import { IError } from '../../../../interfaces';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';

interface IRingostatControlForm {
  api_key: FormControl;
}

@Component({
  selector: 'app-marketplace',
  templateUrl: './market.component.html',
  styleUrls: ['./market.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MarketComponent implements OnInit, OnDestroy {
  // Destroy relative
  public form!: FormGroup<IRingostatControlForm>;
  public isFormSubmitted: boolean = false;
  private readonly destroy$: Subject<null> = new Subject<null>();
  private readonly reasonQuery: string = this.activatedRoute.snapshot.queryParams['reason'];

  public isLoader: boolean = false;
  public applications: any[] = [];

  constructor(
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly notificationService: NotificationService,
    public readonly baseService: BaseService,
    public readonly marketService: MarketService,
    public readonly modalService: ModalService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.form = new FormGroup<IRingostatControlForm>({
      api_key: new FormControl('', Validators.required),
    });
    this.baseService.checkPermission(PERMISSIONS.APPS_ACCESS);

    this.initQuery();
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private init() {
    this.isLoader = true;
    this.marketService
      .getApplications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const items = res;
          items.forEach((v: any) => (v.is_loader = false));
          this.applications = [...items];
          this.isLoader = false;
        },
        error: (e) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  openVchasnoSettings(): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.VchasnoKasaSettings, width: '760px' }, {})
      .subscribe();
  }

  onClick(event: string, is_install = true) {
    if (!is_install) return;
    if (event === APPLICATION_TYPE.VCHASNO_KASA) this.openVchasnoSettings();
    if (event === APPLICATION_TYPE.OLX) this.router.navigate(['/crm/application/olx']);
    if (event === APPLICATION_TYPE.RINGOSTAT) this.router.navigate(['/crm/application/ringostat']);
    if (event === APPLICATION_TYPE.TELEGRAM_BOT)
      this.stackedSidebarService
        .openSidebar({ type: SidebarType.IntegrationTelegram, maxWidth: '50vw' }, {})
        .subscribe();
    if (event === APPLICATION_TYPE.CHECKBOX)
      this.stackedSidebarService
        .openSidebar({ type: SidebarType.IntegrationCheckbox, maxWidth: '50vw' }, {})
        .subscribe();
    if (event === APPLICATION_TYPE.LOGISTIC) this.router.navigate(['/crm/logistic/settings']);
    if (event === APPLICATION_TYPE.GEN_AI)
      this.stackedSidebarService.openSidebar({ type: SidebarType.IntegrationAi }, {}).subscribe();
    if (event === APPLICATION_TYPE.NOVA_POSHTA) this.openNovaPoshtaSettings();
    if (event === APPLICATION_TYPE.NEURON_AND_ANOTHER_CRM)
      this.stackedSidebarService
        .openSidebar({ type: SidebarType.NeuronAndAnotherCrm, maxWidth: '50vw' }, {})
        .subscribe();
  }

  onDownload = (item: any, e: any) => {
    item.is_loader = true;
    this.marketService
      .install(item.id)
      .pipe(takeUntil(this.destroy$))
      .pipe(switchMap(() => this.baseService.getCompanyInfo()))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            activeTranslations.notifications_texts.success.success,
            activeTranslations.notifications_texts.success.successfully_installed,
          );
          this.init();
          // The application does nothing until a cabinet with its API key is added
          if (item.key === APPLICATION_TYPE.NOVA_POSHTA) this.openNovaPoshtaSettings();
          if (item.key === APPLICATION_TYPE.VCHASNO_KASA) this.openVchasnoSettings();
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
          this.init();
        },
      });
  };

  onDelete(item: any) {
    this.marketService
      .uninstall(item.id)
      .pipe(takeUntil(this.destroy$))
      .pipe(switchMap(() => this.baseService.getCompanyInfo()))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_deleted,
          );
          this.init();
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.something_went_wrong_1,
          );
          this.init();
        },
      });
  }

  private openNovaPoshtaSettings(): void {
    this.stackedSidebarService.openSidebar({ type: SidebarType.NovaPoshtaSettings, width: '560px' }, {}).subscribe();
  }

  onConfigure(app: any) {
    if (app.key === APPLICATION_TYPE.VCHASNO_KASA) {
      this.openVchasnoSettings();
    } else if (app.key === APPLICATION_TYPE.NOVA_POSHTA) {
      this.openNovaPoshtaSettings();
    } else if (app.key === 'TELEGRAM_BOT') {
      this.stackedSidebarService
        .openSidebar({ type: SidebarType.IntegrationTelegram, maxWidth: '50vw' }, {})
        .subscribe();
    } else {
      this.stackedSidebarService
        .openSidebar({ type: SidebarType.IntegrationRingostat, maxWidth: '50vw' }, {})
        .subscribe();
    }
  }

  protected readonly parseCurrency = parseCurrency;

  onPay(app: any) {
    this.router.navigate(['/crm/payment']);
  }

  protected readonly APPLICATION_STATUS = APPLICATION_STATUS;

  private initQuery() {
    if (this.reasonQuery === 'NOT_INSTALL') {
      this.notificationService.showWarning(
        '',
        activeTranslations.notifications_texts.warn.application_not_installed,
      );
      this.router.navigate([], { queryParams: {} });
    } else {
      // this.tabs.forEach(v => v.active = false);
      // this.tabs[0].active = true;
      // this.onChangeTab(this.tabs[0]);
    }
  }

  protected readonly moment = moment;
}
